/**
 * PhotoViewerDialog — in-app preview for clock-in photos and face templates.
 *
 * The URL is short-lived and presigned. The viewer intentionally provides no
 * download action, keeping the default workflow focused on attendance review.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

interface PhotoViewerDialogProps {
  open: boolean;
  url: string | null | undefined;
  title?: string;
  /** Optional one-line caption rendered under the image (e.g. metadata). */
  subtitle?: string;
  onClose: () => void;
}

interface PhotoContentProps {
  url: string;
  alt: string;
  loadingLabel: string;
  errorLabel: string;
}

const PhotoContent: React.FC<PhotoContentProps> = ({
  url,
  alt,
  loadingLabel,
  errorLabel,
}) => {
  const [state, setState] = useState<'loading' | 'loaded' | 'error'>('loading');

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 320,
        maxHeight: '70vh',
        overflow: 'auto',
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'grey.100',
      }}
    >
      {state === 'loading' && (
        <Stack
          alignItems="center"
          spacing={1}
          sx={{ position: 'absolute', inset: 0, justifyContent: 'center' }}
        >
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary">
            {loadingLabel}
          </Typography>
        </Stack>
      )}
      {state !== 'error' && (
        <Box
          component="img"
          src={url}
          alt={alt}
          sx={{
            maxWidth: '100%',
            maxHeight: '70vh',
            objectFit: 'contain',
            display: 'block',
            opacity: state === 'loaded' ? 1 : 0,
          }}
          onLoad={() => setState('loaded')}
          onError={() => setState('error')}
        />
      )}
      {state === 'error' && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: 'center', p: 4 }}
        >
          {errorLabel}
        </Typography>
      )}
    </Box>
  );
};

const PhotoViewerDialog: React.FC<PhotoViewerDialogProps> = ({
  open,
  url,
  title,
  subtitle,
  onClose,
}) => {
  const { t } = useTranslation();
  const dialogLabel = title ?? t('common:photoViewer.dialogLabel');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        'aria-label': dialogLabel,
        sx: { borderRadius: 2, bgcolor: 'background.default' },
      }}
    >
      <DialogContent sx={{ py: 2, position: 'relative' }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ mb: 1 }}
        >
          <Box sx={{ minWidth: 0 }}>
            {title && (
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, fontSize: '0.875rem' }}
                noWrap
              >
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', fontSize: '0.75rem' }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          <IconButton
            size="small"
            aria-label={t('common:photoViewer.closeAriaLabel')}
            onClick={onClose}
            sx={{ flexShrink: 0, ml: 1 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        {url ? (
          <PhotoContent
            key={`${open ? 'open' : 'closed'}:${url}`}
            url={url}
            alt={title ?? t('common:photoViewer.photoAlt')}
            loadingLabel={t('common:photoViewer.loading')}
            errorLabel={t('common:photoViewer.expired')}
          />
        ) : (
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="body2" color="text.disabled">
              {t('common:photoViewer.noPhoto')}
            </Typography>
          </Box>
        )}

        <Typography
          variant="caption"
          color="text.disabled"
          sx={{
            display: 'block',
            mt: 1.5,
            fontSize: '0.7rem',
            textAlign: 'center',
          }}
        >
          {t('common:photoViewer.secureStorage')}
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default PhotoViewerDialog;
export { PhotoViewerDialog };
